<template>
  <div style="margin:20px">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Driver Scorecard Comparison</span><br/>
      <span style="font-style: italic; font-size: 18px">Compare multiple driver scorecards side-by-side using the same metrics as Driver Scorecard</span>
    </div>

    <div style="position: relative; font-size: 20px; font-weight: bold;" class="align-right d-flex align-items-center justify-content-center">
      <a href="#" class="white-link" v-on:click.prevent="shift_date_range(-1)"><</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="optionsFrom" :placeholder="details_fromDate"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="optionsTo" :placeholder="details_toDate"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_date_range(1)">></a>
      &nbsp;&nbsp;|&nbsp;&nbsp;
      <b-button v-on:click.prevent="load_scorecards()" :disabled="loading || valid_driver_ids.length === 0">Load Comparison</b-button>
    </div>

    <div v-if="includes_current_month && fleet_avg_selected" class="text-center text-muted mb-2" style="font-style: italic;">
      Fleet Average excludes the current month — pre-calculated data only.
    </div>
    <div v-else-if="includes_current_month" class="text-center text-muted mb-2" style="font-style: italic;">
      Current month data is calculated on-the-fly and may take longer to load.
    </div>

    <div class="driver-picker-strip">
      <span style="font-weight: bold; font-size: 1.1rem;">Drivers:</span>
      <div v-if="driver_options.length > 0" class="driver-picker-scroll">
        <div v-for="(sel, idx) in driver_selections" :key="idx" class="driver-picker-item">
          <b-form-select
            v-model="driver_selections[idx]"
            :options="[{ value: null, text: 'Select Driver' }, ...driver_options]"
            class="scorecard-driver-select"
            size="sm"
          />
          <b-button v-if="driver_selections.length > 1" size="sm" variant="outline-danger" v-on:click.prevent="remove_driver(idx)" class="ml-1">-</b-button>
        </div>
        <b-button size="sm" variant="outline-success" v-on:click.prevent="add_driver()" class="ml-1">+</b-button>
      </div>
      <span v-else><b-spinner style="width: 2rem; height: 2rem;" label="Loading..."></b-spinner></span>
    </div>

    <div v-if="loading" style="text-align: center; margin: 50px;">
      <b-spinner style="width: 2rem; height: 2rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else-if="scorecards.length > 0" class="comparison-table-wrap">
      <p style="font-size: 13px; margin-bottom: 6px; text-align: center;">
        {{ details_fromDate }} to {{ details_toDate }}
        <span v-if="scorecards.some(sc => sc.data.current_month_included)" class="text-muted ml-1">— Current month computed on-the-fly; last 5 days excluded.</span>
      </p>
      <table class="comparison-table">
        <thead>
          <tr>
            <th class="comparison-label-col"></th>
            <th v-for="sc in scorecards" :key="'hdr-'+sc.driver_id" class="comparison-driver-col" :class="{ 'fleet-avg-col': sc.driver_id === '__fleet_avg__' }">
              {{ sc.driver_name || sc.data.driver_name || 'Driver' }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in metric_rows" :key="row.key" :class="{ 'section-header': row.section }">
            <td class="comparison-label-cell" :class="{ 'section-label': row.section }">{{ row.label }}</td>
            <td v-for="sc in scorecards" :key="'val-'+sc.driver_id+'-'+row.key" class="comparison-value-cell" :class="{ 'fleet-avg-col': sc.driver_id === '__fleet_avg__' }">
              <template v-if="!row.section">
                <span :class="row.cssClass ? row.cssClass(sc.data) : ''">{{ row.format(sc.data) }}</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex"
import { justDate } from "../helpers/dates"
import moment from "moment"
import { get_driver_scorecard, get_fleet_scorecard, tbn_get_drivers_scorecard_list } from "../networking/tbn_apis"
import { mergeThresholds, getFieldClass, getCompiledScoreComparisonClass } from "../utils/scorecard_thresholds"

export default {
  name: "driver_scorecard_comparison",

  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    fleet_avg_selected() {
      return this.driver_selections.includes('__fleet_avg__')
    },
    includes_current_month() {
      const current = moment().format("YYYY-MM")
      const startM = moment(this.details_fromDate, "MM-DD-YYYY").format("YYYY-MM")
      const endM = moment(this.details_toDate, "MM-DD-YYYY").format("YYYY-MM")
      return startM <= current && endM >= current
    },
    optionsFrom() {
      const minD = moment().subtract(3, "years").startOf("month")
      const maxD = moment()
      return {
        format: "MM-DD-YYYY",
        useCurrent: true,
        minDate: minD,
        maxDate: maxD,
        enabledDates: this.buildFirstOfMonthDates(minD, maxD),
        keepOpen: false,
        showClear: true,
        showClose: true,
      }
    },
    optionsTo() {
      const minD = moment().subtract(3, "years").startOf("month")
      const maxD = moment()
      return {
        format: "MM-DD-YYYY",
        useCurrent: true,
        minDate: minD,
        maxDate: maxD,
        enabledDates: this.buildLastOfMonthOrTodayDates(minD, maxD),
        keepOpen: false,
        showClear: true,
        showClose: true,
      }
    },
    valid_driver_ids() {
      return this.driver_selections.filter(id => id !== null)
    },
    mergedThresholds() {
      return mergeThresholds(this.scorecard_thresholds)
    },
    metric_rows() {
      const n = (val, dec = 2) => this.formatNumber(val, dec)
      const hasCompiledScore = this.scorecards.some(sc => sc.data.compiled_score !== null && sc.data.compiled_score !== undefined)
      const compiledRows = hasCompiledScore ? [
        { key: 'sec_compiled', label: 'Compiled Performance Score', section: true },
        { key: 'compiled_score', label: 'Score', format: d => d.compiled_score !== null && d.compiled_score !== undefined ? d.compiled_score : 'N/A', cssClass: d => this.getCompiledScoreClass(d.compiled_score) },
        { key: 'compiled_tier', label: 'Tier', format: d => this.getCompiledScoreTier(d.compiled_score) || 'N/A', cssClass: d => this.getCompiledTierTextClass(this.getCompiledScoreTier(d.compiled_score)) },
      ] : []
      return [
        ...compiledRows,
        { key: 'sec_general', label: 'General', section: true },
        { key: 'miles_driven', label: 'Miles Driven', format: d => n(d.miles_driven || d.distance_traveled_miles, 2) },
        { key: 'date_range_days', label: 'Date Range Days', format: d => d.date_range_days || 0 },
        { key: 'sec_risk', label: 'Risk Metrics', section: true },
        { key: 'risk_score', label: 'Risk Score', format: d => d.risk_score || 'N/A', cssClass: d => getFieldClass('risk_score', d.risk_score, this.mergedThresholds) },
        { key: 'risk_rank', label: 'Risk Rank', format: d => d.risk_score_rank || 'N/A', cssClass: d => getFieldClass('risk_rank', d.risk_score_rank, this.mergedThresholds, { total_drivers: this.total_drivers }) },
        { key: 'speeding_pct', label: 'Speeding %', format: d => n(d.speeding_percentage, 2) + '%', cssClass: d => getFieldClass('speeding_percentage', d.speeding_percentage, this.mergedThresholds) },
        { key: 'distracted', label: 'Distracted / 10k', format: d => n(d.distracted_driving_percentage, 2), cssClass: d => getFieldClass('distracted_driving_percentage', d.distracted_driving_percentage, this.mergedThresholds) },
        { key: 'following_dist', label: 'Following Dist / 10k', format: d => n(d.following_distance_per_10k_miles, 2), cssClass: d => getFieldClass('following_distance_per_10k_miles', d.following_distance_per_10k_miles, this.mergedThresholds) },
        { key: 'sec_idle', label: 'Idling & Fuel', section: true },
        { key: 'idling_pct', label: 'Idling %', format: d => n(d.idling_percentage, 2) + '%', cssClass: d => getFieldClass('idling_percentage', d.idling_percentage, this.mergedThresholds) },
        { key: 'mpg', label: 'MPG', format: d => n(d.miles_per_gallon, 2) || 'N/A', cssClass: d => d.miles_per_gallon == null ? 'text-danger' : getFieldClass('miles_per_gallon', d.miles_per_gallon, this.mergedThresholds) },
        { key: 'idle_hrs', label: 'Idle Hrs', format: d => n(d.idle_hours, 2), cssClass: d => getFieldClass('idle_hours', d.idle_hours, this.mergedThresholds) },
        { key: 'run_hrs', label: 'Run Hrs', format: d => n(d.run_hours, 2) },
        { key: 'gallons', label: 'Gallons', format: d => n(d.fuel_consumed_gallons, 2), cssClass: d => getFieldClass('fuel_consumed_gallons', d.fuel_consumed_gallons, this.mergedThresholds) },
        { key: 'sec_dispatch', label: 'Dispatch (TBN)', section: true },
        { key: 'cust_req', label: 'Customer Requests', format: d => d.customer_requests || 0, cssClass: d => getFieldClass('customer_requests', d.customer_requests || 0, this.mergedThresholds) },
        { key: 'bus_days', label: 'Bus Days', format: d => d.bus_days || 0, cssClass: d => getFieldClass('bus_days', d.bus_days, this.mergedThresholds) },
        { key: 'dispatch_miles', label: 'Dispatch Miles', format: d => n(d.total_miles, 2) },
        { key: 'weekend_avail', label: 'Weekend Avail %', format: d => n(d.weekend_availability_percentage, 2) + '%', cssClass: d => getFieldClass('weekend_availability_percentage', d.weekend_availability_percentage, this.mergedThresholds) },
        { key: 'cust_req_10k', label: 'Cust Req / 10k', format: d => n(d.customer_requests_per_10k_miles, 2), cssClass: d => getFieldClass('customer_requests_per_10k_miles', d.customer_requests_per_10k_miles || 0, this.mergedThresholds) },
        { key: 'sec_dvir', label: 'DVIR (Vehicle Inspections)', section: true },
        { key: 'pre_trip_dvir', label: 'Pre-Trip DVIRs', format: d => d.pre_trip_dvir_count || 0 },
        { key: 'post_trip_dvir', label: 'Post-Trip DVIRs', format: d => d.post_trip_dvir_count || 0 },
        { key: 'total_dvir', label: 'Total DVIRs', format: d => d.total_dvir_count || 0 },
        { key: 'dvir_rate', label: 'Completion Rate', format: d => n(d.dvir_completion_rate, 1) + '%', cssClass: d => getFieldClass('dvir_completion_rate', d.dvir_completion_rate, this.mergedThresholds) }
      ]
    }
  },

  data() {
    return {
      loading: false,
      driver_selections: [null],
      driver_options: [],
      total_drivers: 0,
      scorecards: [],
      scorecard_thresholds: null,
      details_fromDate: justDate((moment().date() >= 7 ? moment().subtract(1, "month") : moment().subtract(2, "months")).startOf("month")),
      details_toDate: justDate((moment().date() >= 7 ? moment().subtract(1, "month") : moment().subtract(2, "months")).endOf("month")),
    }
  },

  created() {
    this.load_drivers()
  },

  watch: {
    details_toDate() {
      const today = moment()
      if (moment(this.details_toDate, "MM-DD-YYYY").isAfter(today))
        this.details_toDate = justDate(today)

      if (moment(this.details_fromDate, "MM-DD-YYYY") > moment(this.details_toDate, "MM-DD-YYYY"))
        this.details_fromDate = justDate(moment(this.details_toDate, "MM-DD-YYYY").startOf("month"))

      const months = moment(this.details_toDate, "MM-DD-YYYY").diff(moment(this.details_fromDate, "MM-DD-YYYY"), "months", true)
      if (months > 12) {
        this.details_fromDate = moment(this.details_toDate, "MM-DD-YYYY").subtract(12, "months").startOf("month").format("MM-DD-YYYY")
      }
    },
    details_fromDate() {
      if (moment(this.details_fromDate, "MM-DD-YYYY") > moment(this.details_toDate, "MM-DD-YYYY"))
        this.details_toDate = justDate(moment(this.details_fromDate, "MM-DD-YYYY").endOf("month"))

      const today = moment()
      if (moment(this.details_toDate, "MM-DD-YYYY").isAfter(today))
        this.details_toDate = justDate(today)

      const months = moment(this.details_toDate, "MM-DD-YYYY").diff(moment(this.details_fromDate, "MM-DD-YYYY"), "months", true)
      if (months > 12) {
        this.details_toDate = moment(this.details_fromDate, "MM-DD-YYYY").add(12, "months").endOf("month").format("MM-DD-YYYY")
      }
    },
  },

  methods: {
    buildFirstOfMonthDates(minD, maxD) {
      const out = []
      let m = minD.clone().startOf("month")
      const lastMonthStart = maxD.clone().startOf("month")
      while (m.isSameOrBefore(lastMonthStart, "month")) {
        const first = m.clone().date(1)
        if (first.isSameOrBefore(maxD, "day") && first.isSameOrAfter(minD, "day")) {
          out.push(first.clone())
        }
        m.add(1, "month")
      }
      return out
    },
    buildLastOfMonthOrTodayDates(minD, maxD) {
      const out = []
      const today = moment().startOf("day")
      let m = minD.clone().startOf("month")
      const lastMonthStart = maxD.clone().startOf("month")
      while (m.isSameOrBefore(lastMonthStart, "month")) {
        const last = m.clone().endOf("month").startOf("day")
        if (last.isSameOrAfter(minD, "day") && last.isSameOrBefore(maxD, "day")) {
          out.push(last.clone())
        }
        if (m.isSame(today, "month") && today.isBefore(last, "day")) {
          out.push(today.clone())
        }
        m.add(1, "month")
      }
      return out
    },

    async load_drivers() {
      try {
        const response = await tbn_get_drivers_scorecard_list(this.user.tenant)
        const { value: { data } } = response
        let drivers = []
        if (Array.isArray(data)) {
          drivers = data
        } else if (data && data.data && Array.isArray(data.data)) {
          drivers = data.data
        }
        const filteredDrivers = drivers.filter(driver => driver.value && driver.value !== "")
        const sorted = [...filteredDrivers].sort((a, b) => {
          const textA = (a.text || a.fullName || a.name || "").toLowerCase()
          const textB = (b.text || b.fullName || b.name || "").toLowerCase()
          return textA.localeCompare(textB)
        })
        this.total_drivers = sorted.length
        this.driver_options = [
          { value: '__fleet_avg__', text: 'Fleet Average' },
          ...sorted.map(driver => ({
            value: driver.value,
            text: driver.text || driver.fullName || driver.name || `${driver.firstName || ""} ${driver.lastName || ""}`.trim()
          }))
        ]
      } catch (error) {
        console.error("Error loading drivers:", error)
        this.driver_options = []
      }
    },

    add_driver() {
      this.driver_selections.push(null)
    },

    remove_driver(idx) {
      this.driver_selections.splice(idx, 1)
    },

    shift_date_range(shift) {
      const newFrom = moment(this.details_fromDate, "MM-DD-YYYY").add(shift, "month")
      let newTo = moment(this.details_toDate, "MM-DD-YYYY").add(shift, "month").endOf("month")
      const today = moment()
      if (newTo.isAfter(today)) newTo = today.clone()
      if (newFrom.isAfter(today)) return
      this.details_fromDate = newFrom.format("MM-DD-YYYY")
      this.details_toDate = newTo.format("MM-DD-YYYY")
    },

    async load_scorecards() {
      if (!this.valid_driver_ids.length) return

      this.loading = true
      this.scorecards = []

      try {
        const start_month = moment(this.details_fromDate, "MM-DD-YYYY").format("YYYY-MM")
        const end_month = moment(this.details_toDate, "MM-DD-YYYY").format("YYYY-MM")

        const requests = this.valid_driver_ids.map(driver_id => {
          const fetch = driver_id === '__fleet_avg__'
            ? get_fleet_scorecard(this.user.tenant, start_month, end_month)
            : get_driver_scorecard(this.user.tenant, driver_id, start_month, end_month)
          return fetch
            .then(response => ({ driver_id, response }))
            .catch(error => ({ driver_id, error }))
        })
        const results = await Promise.all(requests)

        const selectedMap = this.driver_options.reduce((acc, d) => {
          acc[d.value] = d.text
          return acc
        }, {})

        // All drivers in a comparison are for the same tenant, so thresholds are identical
        // across responses. We grab the first non-null one as a representative.
        let firstThresholds = null
        const parsed = results
          .map(item => {
            if (item.error) return null
            const responseData = (item.response && item.response.value && item.response.value.data) || (item.response && item.response.data)
            if (!responseData || responseData.error || !responseData.results) return null
            if (!firstThresholds && responseData.scorecard_thresholds) {
              firstThresholds = responseData.scorecard_thresholds
            }
            return {
              driver_id: item.driver_id,
              driver_name: item.driver_id === '__fleet_avg__' ? 'Fleet Average' : (selectedMap[item.driver_id] || ""),
              data: responseData.results
            }
          })
          .filter(Boolean)

        this.scorecard_thresholds = firstThresholds
        this.scorecards = parsed

        const failed = results.length - parsed.length
        if (failed > 0) {
          this.$bvToast.toast(`${failed} driver scorecard request(s) failed or had no data.`, {
            title: "Partial Results", variant: "warning", solid: true, autoHideDelay: 5000
          })
        }
      } catch (error) {
        console.error("Error loading scorecards:", error)
        this.$bvToast.toast("Error loading scorecard comparison data", {
          title: "Error", variant: "danger", solid: true
        })
      } finally {
        this.loading = false
      }
    },

    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined) return "0.00"
      return Number(value).toFixed(decimals)
    },

    getCompiledTierTextClass(tier) {
      const map = { 'Elite': 'text-success', 'Solid': 'text-primary', 'Needs Coaching': 'text-warning', 'High Risk': 'text-danger' }
      return map[tier] || ""
    },

    getCompiledScoreTier(score) {
      if (score === null || score === undefined) return ''
      const cfg = this.mergedThresholds.compiled_score
      const s = Number(score)
      if (cfg) {
        if (s >= Number(cfg.elite))    return 'Elite'
        if (s >= Number(cfg.solid))    return 'Solid'
        if (s >= Number(cfg.coaching)) return 'Needs Coaching'
        return 'High Risk'
      }
      return ''
    },

    getCompiledScoreClass(score) {
      return getCompiledScoreComparisonClass(score, this.mergedThresholds.compiled_score)
    }
  }
}
</script>

<style scoped>
.white-link {
  color: white;
  text-decoration: none;
  font-size: 24px;
}
.white-link:hover {
  color: #ccc;
}

.driver-picker-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}
.driver-picker-scroll {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 8px;
}
.driver-picker-item {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

.comparison-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 8px;
}
.comparison-table {
  border-collapse: collapse;
  width: auto;
  min-width: 100%;
  font-size: 0.82rem;
  line-height: 1.3;
}
.comparison-table th,
.comparison-table td {
  border: 1px solid #dee2e6;
  padding: 3px 8px;
  white-space: nowrap;
}
.comparison-label-col {
  width: 140px;
  min-width: 140px;
  background: #f8f9fa;
  position: sticky;
  left: 0;
  z-index: 2;
}
.comparison-driver-col {
  min-width: 180px;
  width: 180px;
  text-align: center;
  vertical-align: bottom;
  padding: 6px 4px !important;
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 1;
}
.scorecard-driver-select {
  font-size: 0.8rem;
  width: 170px;
  padding: 2px 4px;
}
.comparison-label-cell {
  background: #f8f9fa;
  font-weight: 600;
  position: sticky;
  left: 0;
  z-index: 1;
}
.comparison-label-cell.section-label {
  font-weight: 700;
  font-size: 0.85rem;
  background: #e2e6ea;
  color: #333;
}
.comparison-value-cell {
  text-align: center;
  font-variant-numeric: tabular-nums;
}
tr.section-header td {
  background: #e2e6ea;
}
.fleet-avg-col {
  background: #fff8e1 !important;
  font-weight: 600;
}
tr.section-header .fleet-avg-col {
  background: #ffe082 !important;
}
@media (max-width: 768px) {
  .comparison-driver-col {
    min-width: 150px;
    width: 150px;
  }
  .scorecard-driver-select {
    width: 140px;
  }
}
</style>
